import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { Component, Input, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AuthService } from '../../../services/auth.service';
import { ConfigAddDialogComponent } from './config-add-dialog.component';
import { ConfigDeactivateDialogComponent } from './config-deactivate-dialog.component';

export interface ConfigColumnDef {
  field: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'textarea';
  required?: boolean;
  maxLength?: number;
  selectOptions?: { value: any; label: string }[];
  editable?: boolean;
  sortable?: boolean;
}

export interface ConfigRow {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  isSystem?: boolean;
  sortOrder?: number;
  [key: string]: any;
}

@Component({
  selector: 'app-config-table',
  templateUrl: './config-table.component.html',
  styleUrls: ['./config-table.component.css'],
})
export class ConfigTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tableName = '';
  @Input() columns: ConfigColumnDef[] = [];
  @Input() permissionKey = '';
  @Input() sortField = 'name';
  @Input() loadFn!: () => Observable<any[]>;
  @Input() createFn?: (data: any) => Observable<any>;
  @Input() updateFn?: (id: any, data: any) => Observable<any>;
  @Input() deactivateFn?: (id: any) => Observable<void>;
  /** Permanent delete. The server cascades: rows referencing the item are
   *  updated (references cleared or reassigned) before the item is removed. */
  @Input() deleteFn?: (id: any) => Observable<{ message?: string } | void>;
  @Input() reorderFn?: (order: { id: number; sortOrder: number }[]) => Observable<void>;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<ConfigRow>([]);
  loading = false;
  saving = false;
  showInactive = false;
  filterText = '';
  editingId: number | null = null;
  editData: Partial<ConfigRow> = {};

  private allRows: ConfigRow[] = [];
  private readonly destroyed$ = new Subject<void>();
  private retrySubscription?: Subscription;

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Catalog view'), name: 'Catalog view', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ search: this.filterText, showInactive: this.showInactive }),
      describe: state => JSON.stringify(state),
      restore: state => { this.filterText = state.search; this.showInactive = state.showInactive; this.applyFilter(); },
    });
  }

  constructor(
    public auth: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.registerViewContext();
    this.dataSource.filterPredicate = (row, filter) =>
      JSON.stringify(row).toLowerCase().includes(filter);
    this.load();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.retrySubscription?.unsubscribe();
  }

  get canWrite(): boolean {
    return this.auth.hasPermission(this.permissionKey);
  }

  get displayedColumns(): string[] {
    const canDrag = this.canWrite && !!this.reorderFn;
    const dataCols = this.columns.map(c => c.field);
    return [
      ...(canDrag ? ['drag'] : []),
      ...dataCols,
      'isActive',
      ...(this.canWrite ? ['actions'] : []),
    ];
  }

  load(): void {
    this.retrySubscription?.unsubscribe();
    this.loading = true;
    this.loadFn().subscribe({
      next: rows => {
        const sf = this.sortField;
        this.allRows = [...rows].sort(
          (a, b) =>
            (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999) ||
            (a[sf] ?? '').localeCompare(b[sf] ?? ''),
        );
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.retrySubscription = this.snackBar
          .open(`Failed to load ${this.tableName}`, 'Retry', { duration: 6000 })
          .onAction()
          .subscribe(() => this.load());
      },
    });
  }

  applyFilter(): void {
    this.dataSource.data = this.showInactive
      ? this.allRows
      : this.allRows.filter(r => r.isActive !== false);
  }

  applySearch(): void {
    this.dataSource.filter = this.filterText.trim().toLowerCase();
  }

  startEdit(row: ConfigRow): void {
    if (row.isSystem) return;
    this.editingId = row.id;
    this.editData = { ...row };
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editData = {};
  }

  submitEdit(): void {
    if (!this.updateFn || this.editingId == null) return;
    this.saving = true;
    this.updateFn(this.editingId, this.editData).subscribe({
      next: () => {
        this.saving = false;
        this.editingId = null;
        this.editData = {};
        this.load();
        this.snackBar.open('Saved', undefined, { duration: 2000 });
      },
      error: (e: any) => {
        this.saving = false;
        this.snackBar.open(e?.error?.error ?? e?.error?.message ?? 'Failed to save', 'OK', { duration: 5000 });
      },
    });
  }

  openAddDialog(): void {
    const ref = this.dialog.open(ConfigAddDialogComponent, {
      width: '460px',
      panelClass: 'config-add-dialog-panel',
      data: { tableName: this.tableName, columns: this.columns },
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (!result || !this.createFn) return;
      this.saving = true;
      this.createFn(result).subscribe({
        next: () => {
          this.saving = false;
          this.load();
          this.snackBar.open('Added', undefined, { duration: 2000 });
        },
        error: (e: any) => {
          this.saving = false;
          this.snackBar.open(e?.error?.error ?? e?.error?.message ?? 'Failed to add', 'OK', { duration: 5000 });
        },
      });
    });
  }

  openDeactivateDialog(row: ConfigRow): void {
    if (row.isSystem) return;
    const ref = this.dialog.open(ConfigDeactivateDialogComponent, {
      width: '420px',
      data: { row, deactivateFn: this.deactivateFn },
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(confirmed => {
      if (confirmed) {
        this.load();
        this.snackBar.open('Deactivated', undefined, { duration: 2000 });
      }
    });
  }

  deleteRow(row: ConfigRow): void {
    if (!this.deleteFn || row.isSystem) return;
    const ok = confirm(
      `Permanently delete "${row.name}"?\n\n` +
      `Records using it (customers, billing, …) will be updated automatically — ` +
      `their reference is cleared or reassigned. This cannot be undone.`);
    if (!ok) return;
    this.deleteFn(row.id).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (r: any) => {
        this.load();
        this.snackBar.open(r?.message ?? 'Deleted', undefined, { duration: 3500 });
      },
      error: (e: any) => {
        this.snackBar.open(e?.error?.error ?? e?.error?.message ?? 'Delete failed', 'OK', { duration: 5000 });
      },
    });
  }

  drop(event: CdkDragDrop<ConfigRow[]>): void {
    // CDK indices are page-local; offset by current paginator page to get global position
    const pageOffset = this.paginator
      ? this.paginator.pageIndex * this.paginator.pageSize
      : 0;
    const data = [...this.dataSource.data];
    moveItemInArray(data, event.previousIndex + pageOffset, event.currentIndex + pageOffset);
    const withOrder = data.map((row, i) => ({ ...row, sortOrder: i + 1 }));
    this.dataSource.data = withOrder;
    this.allRows = this.allRows.map(r => withOrder.find(w => w.id === r.id) ?? r);
    if (this.reorderFn) {
      this.reorderFn(withOrder.map(r => ({ id: r.id, sortOrder: r.sortOrder! }))).subscribe({
        error: () =>
          this.snackBar.open('Failed to reorder', 'OK', { duration: 4000 }),
      });
    }
  }

  readonly skeletonRows = [1, 2, 3, 4];

  getDisplayValue(col: ConfigColumnDef, value: any): string {
    const opt = col.selectOptions?.find(o => o.value === value);
    return opt ? opt.label : String(value ?? '');
  }
}
